<template>
  <div>
    <v-overlay :value="isBusy">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-card-text>
      <div class="d-flex my-auto">
        <img style="vertical-align: middle;" class="mr-2" src="@/assets/img/gba-logox48.png" />
        <div class="text-body-1 my-auto mr-2">Genomic Bleed Application</div>
        <v-spacer />
        <div class="mr-2 text-body-1 my-auto ">v{{ appVersion }}</div>
        <v-spacer />
        <img
          class="mt-1"
          src="@/assets/img/cobb-logo.png" 
          alt="Cobb Logo"
          height="38"
        />
      </div>

      <v-divider class="mt-4 mb-2" />

      <div class="d-flex my-auto">
        <div class="mt-2 ml-3 mr-1 text-truncate d-flex">
          User: <div class="primary--text ml-5">{{ userName }}</div>
        </div>
        <v-spacer />
        <v-btn
          class="mt-1 px-2 pr-3"
          color="secondary"
          :disabled="!isOnline"
          @click="signOut()"
        >
          <v-icon class="mr-1">mdi-exit-run</v-icon>
          Sign Out
        </v-btn>
      </div>

      <div class="pl-0 d-flex flex-wrap"> 
        <v-col class="pb-0" :cols="breakpoint === 'xs' ? '5' : '2'">
          <v-select
            v-model="currentFarm"
            label="Current Farm"
            :disabled="!isOnline"
            :items="farms"
          />
        </v-col>
        <v-spacer />
        <v-btn
          color="primary"
          class="mt-5 white--text"
          @click="reloadApp()"
          :disabled="!isOnline">
          <v-icon class="mr-1">mdi-reload</v-icon>
          Reload
        </v-btn>
      </div>
      <div class="ml-3 d-flex flex-wrap text-truncate">Last Server Update: 
        <div class="ml-5 primary--text">{{ lastServerLoadTime ?? 'N/A' }}</div>
      </div>
      <v-divider class="my-4" />

      <div class="text-subtitle-1">Display:</div>
      <div class="d-flex flex-wrap">
        <v-switch 
          v-model="darkMode"
          inset
          class="pa-0 mt-1 ml-2 mr-2"
          label="Dark Mode"
        />
        <v-switch
          class="pa-0 ml-2 mt-1"
          v-model="isGenomicCheckEnabled"
          inset
          label="Genomic Check"
        />
      </div>
      <div class="d-flex flex-wrap"> 
        <v-btn 
          class="mt-2 white--text"
          color="primary"
          @click="lockOrientation()">
           <v-icon class="mr-1">mdi-lock</v-icon>Lock
        </v-btn>
        <v-col class="pb-0" :cols="breakpoint === 'xs' ? '8' : '4'">
          <v-select
            class="ml-3 pa-0"
            v-model="orientation"
            label="Orientation"
            :items="orientationTypes"
          />
        </v-col>
      </div>

      <v-divider class="mt-0 mb-4" />

      <div class="my-0">
        <div class="text-subtitle-1">Sounds:</div>
        <div class="d-flex">
          <v-switch 
            class="mt-1 ml-1"
            v-model="isSuccessSoundEnabled"
            inset
            label="Success Sound"
          />
          <v-spacer />
          <v-btn
            color="primary"
            class="white--text"
            :disabled="!isSuccessSoundEnabled"
            @click="makeSuccessSound()"
          >
            <v-icon>mdi-volume-high</v-icon>
            <div>Success</div>
          </v-btn>
        </div>
        <div class="d-flex">
          <v-switch
            class="mt-1 ml-1"
            v-model="isErrorSoundEnabled"
            inset
            label="Error Sound"
          />
          <v-spacer />
          <v-btn
            :disabled="!isErrorSoundEnabled"
            color="primary"
            class="white--text"
            @click="makeErrorSound()"
          >
            <v-icon>mdi-volume-high</v-icon>
            <div class="pr-5">Error</div>
          </v-btn>
        </div>
      </div>

      <v-divider class="mb-3" />

      <v-btn
        color="primary"
        class="mt-0 white--text"
        @click="showBackupDialog = true"
        :disabled="isBackupCapabilityAvailable == false"
      >
        Data Backup
      </v-btn>
      <span class="ml-4 text-subtitle-2 red--text" v-if="isBackupCapabilityAvailable == false">Not available for this device!</span>
      <v-divider class="mt-4 mb-4" />

      <div class="ml-2 text-subtitle-1 text-no-wrap">DS9808 Scanner Barcodes:</div>
      <div class="d-flex ml-2">
        <v-card class="px-4">
          Regular Read
          <v-img
            src="@/assets/img/DS9808-Barcode-Regular.png"
            class="my-2"
            contain
            height="68"
          />
        </v-card>
        <v-card class="ml-4 px-4">
          Continuous Read
          <v-img
            src="@/assets/img/DS9808-Barcode-Continuous.png"
            class="my-2"
            contain
            height="68"
          />
        </v-card>
      </div>

      <v-divider class="my-4" />

      <div>
        <div class="ml-2 text-subtitle-1 text-no-wrap">Environment:</div>
        <div style="word-break: break-word" class="text-subtitle-2 text-truncate ml-5 mt-1">{{ environment }}</div>
      </div>
      <div>
        <div class="ml-2 text-subtitle-1 text-no-wrap">Biotech Url:</div>
        <div style="word-break: break-word" class="text-subtitle-2  text-truncate ml-5 mt-1">{{ apiUrl }}</div>
      </div>
      <div>
        <div class="ml-2 text-subtitle-1 text-no-wrap">QAP Url:</div>
        <div style="word-break: break-word" class="text-subtitle-2  text-truncate ml-5 mt-1">{{ qapUrl }}</div>
      </div>
      <div>
        <div class="ml-2 text-subtitle-1 text-no-wrap">Azure Storage:</div>
        <div style="word-break: break-word" class="text-subtitle-2  text-truncate ml-5 mt-1">
          <a :href="biotechStorageUrl"
            target="_blank">Biotech Blob Storage Container {{ isProductionEnv ? '' : ' - Dev' }}
          </a>
        </div>
      </div>
    </v-card-text>

    <v-dialog v-model="showBackupDialog" persistent max-width="380">
      <v-card color="cardbackground">
        <v-card-title class="text-h5">Data Backup Settings</v-card-title>
        <v-card-text class="pb-0">
          <div>
              <u>NOTE</u>: When enabling a backup, select a local drive (removable if available)
              and a directory location. The drive (name and letter) may be different from one device to another. 
              Below are example settings:
              <ul>
                <li><b>Genomic:</b> <i>SDXC(F:) > GBA > Genomic Backups</i></li>
                <li><b class="mr-4">Health:</b> <i>SDXC(F:) > GBA > Health Backups</i></li>
              </ul>
              <v-divider class="my-4" />
              <v-switch 
                class="mt-1 ml-5"
                v-model="isGenomicBackupEnabled"
                inset
                label="Genomic Backup Enabled"
              />
              <v-switch 
                class="mt-1 ml-5 pt-0"
                v-model="isHealthBackupEnabled"
                inset
                label="Health Backup Enabled"
              />
          </div>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="saveBackupSettings()">Save</v-btn>
          <v-btn color="secondary" @click="cancelBackupSettings()">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import gbaPackage from '../../package.json';
import { mapState, mapActions } from 'vuex';
import fileSysApi from '@/api/fileSysApi';
import OfflineStorage from '../offlineStorage';

export default {
  data() {
    return {
      isProductionEnv: null,
      lastServerLoadTime: null,
      appVersion: gbaPackage.version,
      genomicBackupDir: null,
      genomicBackupEnabledFlag: false,
      healthBackupDir: null,
      healthBackupEnabledFlag: false,
      isBusy: false,
      orientation: "natural",
      orientationTypes: ['natural', 'portrait-primary', 'portrait-secondary', 'landscape-primary', 'landscape-secondary'],
      showBackupDialog: false
    }
  },
  async mounted() {
      window.addEventListener("orientationchange", this.handleOrientationChange);
      this.genomicBackupDir = this.settings.genomicBackupDir;
      this.genomicBackupEnabledFlag = !!this.settings.genomicBackupDir;

      this.healthBackupDir = this.settings.healthBackupDir;
      this.healthBackupEnabledFlag = !!this.settings.healthBackupDir;

      this.lastServerLoadTime = await OfflineStorage.getItem('lastServerLoadTime');
  },
  async created() {
    this.orientation = screen.orientation.type;
    this.isProductionEnv = this.environment == 'production';
  },
  computed: {
    ...mapState(['settings', 'isOnline', 'userProfile']),
    apiUrl() {
      return process.env.VUE_APP_ROOT_API_SERVER_URL;
    },
    biotechStorageUrl() {
      let storageContainer = 'biotech';
      if (this.isProductionEnv === false) {
        storageContainer += '-dev';
      }
      const location = `storageexplorer://?v=2` +
        `&tenantId=da45342c-ce89-44fe-82c9-2c8502e05791` +
        `&container=${storageContainer}` +
        `&type=blobContainer` +
        `&serviceEndpoint=https://cviitrd.blob.core.windows.net/` +
        `&subscriptionId=c135ab29-a6e6-4748-b933-43de01c1358d` +
        `&storageAccountId=cviitrd`;
      return location;
    },
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    currentFarm: {
      get() { return this.settings.currentFarm; },
      set(value) {
        const settings = { ...this.settings };
        settings.currentFarm = value;
        this.loadFarmSettings(settings);
      }
    },
    darkMode: {
      get() { return this.settings.isDarkMode === true; },
      set(value) {
        const settings = { ...this.settings };
        settings.isDarkMode = value;
        this.updateSettingsInStore(settings);
        this.$vuetify.theme.dark = value;
        window.dispatchEvent(new CustomEvent('gba-set-scrollbar-theme'));
      }
    },
    environment() {
      return process.env.VUE_APP_MODE;
    },
    farms() {
      return this.settings.farmLineMg.map(x=> x.farm);
    },
    isErrorSoundEnabled: {
      get() { return this.settings.isErrorSoundEnabled === true; },
      set(value) {
        const settings = { ...this.settings };
        settings.isErrorSoundEnabled = value;
        this.updateSettingsInStore(settings);
      }
    },
    isSuccessSoundEnabled: {
      get() { return this.settings.isSuccessSoundEnabled === true; },
      set(value) {
        const settings = { ...this.settings };
        settings.isSuccessSoundEnabled = value;
        this.updateSettingsInStore(settings);
      }
    },
    isGenomicCheckEnabled: {
      get() { return this.settings.isGenomicCheckEnabled === true; },
      set(value) {
        const settings = { ...this.settings };
        settings.isGenomicCheckEnabled = value;
        this.updateSettingsInStore(settings);
      }
    },
    isGenomicBackupEnabled: {
      get() { 
        return this.genomicBackupEnabledFlag;
      },
      async set(value) {
        try {
          this.genomicBackupEnabledFlag = value;
          if (value) this.genomicBackupDir = await window.showDirectoryPicker();
          else this.genomicBackupDir = null;
        }
        catch (error) {
          this.genomicBackupDir = null;
          this.genomicBackupEnabledFlag = false;
        }
      }
    },
    isHealthBackupEnabled: {
      get() { 
        return this.healthBackupEnabledFlag;
      },
      async set(value) {
        try {
          this.healthBackupEnabledFlag = value;
          if (value) this.healthBackupDir = await window.showDirectoryPicker();
          else this.healthBackupDir = null;
        }
        catch (error) {
          this.healthBackupDir = null;
          this.healthBackupEnabledFlag = false;
        }
      }
    },
    isBackupCapabilityAvailable() {
      return fileSysApi.isAvailable;
    },
    qapUrl() {
      return process.env.VUE_APP_QAP_API_SERVER_URL;
    },
    userName() {
      if (this.userProfile) {
        var userName = this.userProfile.userPrincipalName.split('@')[0];
        return userName;
      }
      else return null;
    }
  },
  methods: {
    ...mapActions(['updateSettingsInStore', 'loadFarmSettingsFromServer', 'storeSignOut']),
    async signOut() {
      await this.storeSignOut();
    },
    handleOrientationChange() {
      this.orientation = screen.orientation.type;
    },
    async loadFarmSettings(settings) {
      try {
        this.isBusy = true;

        await this.loadFarmSettingsFromServer({settings, root: this.$root});
        const loadDate = new Date();
        
        await OfflineStorage.setItem('lastServerLoadTime', loadDate);
        //sessionStorage.setItem('lastServerLoadTime', loadDate);
        this.lastServerLoadTime = loadDate;

        this.isBusy = false;
      }
      catch (error) {
        this.isBusy = false;
        this.$root.showNoticeError(error.message);
      }
    },
    makeSuccessSound() {
      if (this.isSuccessSoundEnabled) {
        const audio = new Audio('sound/success-chime.mp3');
        audio.play();
      }
    },
    async lockOrientation() {
      try {
        if (this.orientation == "natural") {
          document.webkitExitFullscreen();
          await screen.orientation.unlock();
        }
        else {
          const body = document.getElementsByTagName("BODY")[0];
          body.webkitRequestFullscreen();
          await screen.orientation.lock(this.orientation);
        }
      } 
      catch (error) {
        this.orientation = screen.orientation.type;
        this.$root.showNoticeError(error.message);
      }
    },
    makeErrorSound() {
      if (this.isErrorSoundEnabled) {
        const audio = new Audio('sound/error-beep.mp3');
        audio.play();
      }
    },
    reloadApp() {
      window.location = process.env.BASE_URL;
    },
    cancelBackupSettings() {
      this.showBackupDialog = false;
      this.genomicBackupEnabledFlag = !!this.settings.genomicBackupDir;
      this.healthBackupEnabledFlag = !!this.settings.healthBackupDir;
      this.genomicBackupDir = this.settings.genomicBackupDir;
      this.healthBackupDir = this.settings.healthBackupDir;
    },
    saveBackupSettings() {
      const settings = { ...this.settings };

      settings.genomicBackupDir = this.genomicBackupDir;
      settings.healthBackupDir = this.healthBackupDir;

      this.updateSettingsInStore(settings);

      this.showBackupDialog = false;
    }
  }
}
</script>